import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab,
    Alert, CircularProgress, Card, CardContent, Stack, Divider
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    DoneAll as DoneAllIcon,
    Assignment as AssignmentIcon,
    NotificationsActive as NotificationsActiveIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { useCompetence } from '../contexts/CompetenceContext';
import { triggerValidation, getValidationResults, getPersistentAnomalies, updateAnomalyStatus } from '../api/api';

const MonitoringPage = () => {
    const { globalCompetence } = useCompetence();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [validations, setValidations] = useState([]);
    const [anomalies, setAnomalies] = useState([]);
    const [error, setError] = useState('');

    // Resolution Modal
    const [openModal, setOpenModal] = useState(false);
    const [selectedAnomaly, setSelectedAnomaly] = useState(null);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (globalCompetence) {
            loadData();
        }
    }, [globalCompetence, tabValue]);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            if (tabValue === 0) {
                const res = await getValidationResults(globalCompetence);
                setValidations(res.data);
            } else {
                const res = await getPersistentAnomalies(globalCompetence);
                setAnomalies(res.data);
            }
        } catch (err) {
            setError('Erro ao carregar dados de monitoramento.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRunValidation = async () => {
        setLoading(true);
        try {
            await triggerValidation(globalCompetence);
            await loadData();
        } catch (err) {
            setError('Falha ao acionar validadores.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenResolution = (anomaly) => {
        setSelectedAnomaly(anomaly);
        setNotes(anomaly.resolution_notes || '');
        setOpenModal(true);
    };

    const handleResolve = async (status) => {
        try {
            await updateAnomalyStatus(selectedAnomaly.id, status, notes);
            setOpenModal(false);
            loadData();
        } catch (err) {
            alert('Erro ao atualizar status.');
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'success': return <Chip label="Sucesso" color="success" size="small" icon={<CheckCircleIcon />} />;
            case 'warning': return <Chip label="Aviso" color="warning" size="small" icon={<WarningIcon />} />;
            case 'error': return <Chip label="Erro" color="error" size="small" icon={<ErrorIcon />} />;
            case 'resolved': return <Chip label="Resolvido" color="success" size="small" variant="outlined" />;
            case 'dismissed': return <Chip label="Ignorado" color="default" size="small" variant="outlined" />;
            case 'open': return <Chip label="Aberto" color="error" size="small" />;
            default: return <Chip label={status} size="small" />;
        }
    };

    const getSeverityIcon = (severity) => {
        if (severity === 'high' || severity === 'critical') return <ErrorIcon color="error" />;
        if (severity === 'medium') return <WarningIcon color="warning" />;
        return <InfoIcon color="primary" />;
    };

    return (
        <Box p={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight="bold">Segurança e Monitoramento</Typography>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRunValidation}
                    disabled={loading || !globalCompetence}
                >
                    Executar Validadores
                </Button>
            </Stack>

            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
                <Tab icon={<DoneAllIcon />} label="Integridade de Dados" iconPosition="start" />
                <Tab icon={<NotificationsActiveIcon />} label="Gestão de Anomalias" iconPosition="start" />
            </Tabs>

            {loading && <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {!loading && tabValue === 0 && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Resultados da Validação: {globalCompetence}</Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Mensagem</TableCell>
                                    <TableCell>Data</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {validations.map((v) => (
                                    <TableRow key={v.id}>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>{v.type}</TableCell>
                                        <TableCell>{getStatusChip(v.status)}</TableCell>
                                        <TableCell>{v.message}</TableCell>
                                        <TableCell>{new Date(v.created_at).toLocaleString()}</TableCell>
                                    </TableRow>
                                ))}
                                {validations.length === 0 && (
                                    <TableRow><TableCell colSpan={4} align="center">Nenhum resultado disponível para esta competência.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {!loading && tabValue === 1 && (
                <Grid container spacing={3}>
                    {anomalies.map((a) => (
                        <Grid item xs={12} md={6} key={a.id}>
                            <Card variant="outlined" sx={{ borderLeft: 6, borderColor: a.status === 'open' ? 'error.main' : 'success.main' }}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                {a.anomaly_type.toUpperCase()} • {a.competencia}
                                            </Typography>
                                            <Typography variant="h6" display="flex" alignItems="center">
                                                {getSeverityIcon(a.severity)}
                                                <Box component="span" ml={1}>{a.description}</Box>
                                            </Typography>
                                        </Box>
                                        {getStatusChip(a.status)}
                                    </Stack>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button size="small" startIcon={<AssignmentIcon />} onClick={() => handleOpenResolution(a)}>
                                            {a.status === 'open' ? 'Tratar' : 'Ver Notas'}
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {anomalies.length === 0 && (
                        <Grid item xs={12}><Alert severity="info">Nenhuma anomalia persistente detectada para {globalCompetence}.</Alert></Grid>
                    )}
                </Grid>
            )}

            {/* Resolution Modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm">
                <DialogTitle>Tratamento de Anomalia</DialogTitle>
                <DialogContent>
                    <Box mt={1}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Descrição:</Typography>
                        <Typography variant="body1" mb={2}>{selectedAnomaly?.description}</Typography>
                        <TextField
                            label="Notas de Resolução / Justificativa"
                            multiline
                            rows={3}
                            fullWidth
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>Fechar</Button>
                    {selectedAnomaly?.status === 'open' && (
                        <>
                            <Button onClick={() => handleResolve('dismissed')} color="warning">Ignorar</Button>
                            <Button onClick={() => handleResolve('resolved')} color="success" variant="contained">Marcar como Resolvido</Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MonitoringPage;
